// Deciding whether a signed-out event is real.
//
// onAuthStateChanged(null) does not always mean "this person signed out". The SDK
// emits a null user on ordinary blips: a token refresh that loses the network, a
// backgrounded mobile tab waking up. The app used to act on the first such null —
// wipe `authed` in firebase.js, wipe permissions in App.js — which showed up to users
// as being logged out, or thrown back to the home page, in the middle of their work.
//
// So a null is now a question. Two signals answer it:
//  1. The deliberate flag. Set it before signing out and the sign-out is instant.
//  2. Firebase's own persisted auth record. While `firebase:authUser:*` is still in
//     localStorage the SDK still holds a session, so a null alongside it is a blip.
//     A real sign-out — including a revoked or expired refresh token — removes it.
// A null with no record, and no recovery, is confirmed after the grace window. The
// deadline caps how long a stuck record can keep a dead session alive.
//
// This lives apart from session.js so firebase.js can use it without the two importing
// each other.

const DELIBERATE_SIGN_OUT_KEY = 'deliberateSignOut';
const SIGNED_OUT_GRACE_MS = 12 * 1000;
const SIGNED_OUT_DEADLINE_MS = 90 * 1000;

// localStorage, not sessionStorage: signing out in one tab has to sign out the others,
// and they need to know that null was intentional.
export const markDeliberateSignOut = () => {
  try { localStorage.setItem(DELIBERATE_SIGN_OUT_KEY, String(Date.now())); } catch (e) { /* non-fatal */ }
};

export const clearDeliberateSignOut = () => {
  try { localStorage.removeItem(DELIBERATE_SIGN_OUT_KEY); } catch (e) { /* non-fatal */ }
};

// Only counts for a moment after it was set. A stale flag left behind by a crashed tab
// would make the next ordinary blip look intentional.
const isDeliberateSignOut = () => {
  try {
    const at = Number(localStorage.getItem(DELIBERATE_SIGN_OUT_KEY));
    return !!at && Date.now() - at < 30 * 1000;
  } catch (e) {
    return false;
  }
};

const hasPersistedAuthRecord = () => {
  try {
    return Object.keys(localStorage).some(k => k.startsWith('firebase:authUser:'));
  } catch (e) {
    // No readable localStorage means no way to tell a blip from a sign-out. Assume the
    // sign-out is real so a genuinely signed-out user is not stranded on a dead page.
    return false;
  }
};

// Call this on a null user instead of acting on it. `onConfirmed` runs only if the
// sign-out turns out to be real. Returns a cancel function — call it as soon as a user
// reappears, which is what makes a blip cost nothing.
export const confirmSignedOut = (getCurrentUser, onConfirmed) => {
  const startedAt = Date.now();
  let timer = null;
  let cancelled = false;

  const finish = () => {
    cancelled = true;
    onConfirmed();
  };

  const check = () => {
    if (cancelled) return;
    if (getCurrentUser()) return; // recovered on its own

    const expired = Date.now() - startedAt >= SIGNED_OUT_DEADLINE_MS;
    if (!hasPersistedAuthRecord() || expired) return finish();
    timer = setTimeout(check, SIGNED_OUT_GRACE_MS);
  };

  // A deliberate sign-out is honoured on the next tick rather than right now. Callers
  // assign this function's return value to the same handle they null out inside
  // onConfirmed, so running it synchronously would leave a stale handle behind.
  // The flag is deliberately NOT consumed here: firebase.js and App.js both call this
  // for the same null, and clearing it would leave the second one waiting out the
  // grace period. It expires on its own, and a sign-in clears it.
  timer = isDeliberateSignOut()
    ? setTimeout(finish, 0)
    : setTimeout(check, SIGNED_OUT_GRACE_MS);

  return () => {
    cancelled = true;
    if (timer) clearTimeout(timer);
  };
};
